import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import NotificationsPanel from "@/components/NotificationsPanel";
import MenuViewer from "@/components/MenuViewer";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  Plus, Pencil, Trash2, Wallet, Package, Store, Users, IndianRupee, Clock, LayoutDashboard, Warehouse, AlertTriangle, ArrowRightLeft, Flame, Scale, Upload, Download, Loader2, History,
} from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const COLORS = ["#ea580c", "#f59e0b", "#15803d", "#2563eb", "#db2777"];

export default function AdminView() {
  const [tab, setTab] = useState("analytics");
  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-6">
          <LayoutDashboard className="h-5 w-5 text-primary" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Admin Console</h1>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Dashboard</TabsTrigger>
            <TabsTrigger value="items" data-testid="admin-tab-items">Items</TabsTrigger>
            <TabsTrigger value="cafeterias" data-testid="admin-tab-cafeterias">Cafeterias</TabsTrigger>
            <TabsTrigger value="employees" data-testid="admin-tab-employees">Employees</TabsTrigger>
            <TabsTrigger value="stores" data-testid="admin-tab-stores">Stores</TabsTrigger>
            <TabsTrigger value="operations" data-testid="admin-tab-operations">Operations</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="admin-tab-alerts">Notifications</TabsTrigger>
            <TabsTrigger value="activity" data-testid="admin-tab-activity">Activity Log</TabsTrigger>
            <TabsTrigger value="menu" data-testid="admin-tab-menu">Menu</TabsTrigger>
          </TabsList>
          <TabsContent value="analytics" className="mt-6"><Analytics /></TabsContent>
          <TabsContent value="items" className="mt-6"><ItemsTab /></TabsContent>
          <TabsContent value="cafeterias" className="mt-6"><CafeteriasTab /></TabsContent>
          <TabsContent value="employees" className="mt-6"><EmployeesTab /></TabsContent>
          <TabsContent value="stores" className="mt-6"><StoreAnalytics /></TabsContent>
          <TabsContent value="operations" className="mt-6"><AdminOperations /></TabsContent>
          <TabsContent value="alerts" className="mt-6"><NotificationsPanel admin /></TabsContent>
          <TabsContent value="menu" className="mt-6"><AdminMenuTab /></TabsContent>
          <TabsContent value="activity" className="mt-6"><ActivityLogTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ---------------- Analytics ---------------- */
function Analytics() {
  const [timeframe, setTimeframe] = useState("daily");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/dashboard", { params: { timeframe } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [timeframe]);

  const kpi = data?.kpi;
  const tiles = [
    { label: "Material Dispatched", value: fmtMoney(kpi?.total_value), icon: Wallet, testid: "admin-kpi-total-material-value" },
    { label: "Qty Dispatched", value: `${kpi?.total_kgs ?? 0} Kgs · ${kpi?.total_pcs ?? 0} Pcs`, icon: Package, testid: "admin-kpi-total-dispatched-qty" },
    { label: "Pending Orders", value: kpi?.pending_orders ?? 0, icon: Clock, testid: "admin-kpi-pending" },
    { label: "Cafeterias Served", value: kpi?.cafeterias_served ?? 0, icon: Store, testid: "admin-kpi-cafeterias" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        {["daily", "weekly", "monthly"].map((tf) => (
          <Button key={tf} variant={timeframe === tf ? "default" : "outline"} size="sm"
            onClick={() => setTimeframe(tf)} data-testid={`admin-timeframe-${tf}-button`} className="capitalize">
            {tf}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-5 border-orange-100 dark:border-slate-800" data-testid={t.testid}>
            <t.icon className="h-5 w-5 text-primary mb-3" />
            <p className="text-xs uppercase font-mono tracking-widest text-muted-foreground">{t.label}</p>
            <p className="font-mono text-xl sm:text-2xl font-extrabold mt-1">{t.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2 border-orange-100 dark:border-slate-800">
          <h3 className="font-heading font-bold mb-4">Dispatched Quantity Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data?.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
              <Line type="monotone" dataKey="qty" stroke="#ea580c" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 border-orange-100 dark:border-slate-800">
          <h3 className="font-heading font-bold mb-4">Base Kitchen Load</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data?.base_split || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {(data?.base_split || []).map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 border-orange-100 dark:border-slate-800">
        <h3 className="font-heading font-bold mb-4">Material Value per Cafeteria</h3>
        {(data?.per_cafeteria || []).length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No dispatched orders in this period.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.per_cafeteria || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="cafeteria" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="value" fill="#ea580c" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <Table className="mt-4">
              <TableHeader>
                <TableRow>
                  <TableHead>Cafeteria</TableHead>
                  <TableHead className="text-right">Kgs</TableHead>
                  <TableHead className="text-right">Pcs</TableHead>
                  <TableHead className="text-right">Material Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_cafeteria.map((c) => (
                  <TableRow key={c.cafeteria}>
                    <TableCell className="font-medium">{c.cafeteria}</TableCell>
                    <TableCell className="text-right font-mono">{c.kgs}</TableCell>
                    <TableCell className="text-right font-mono">{c.pcs}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-primary">{fmtMoney(c.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Admin Operations (unified Cafeteria / Kitchen / Store boards) ---------------- */
function AdminOperations() {
  const [view, setView] = useState("cafeteria");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loc, setLoc] = useState("all");
  const [orders, setOrders] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [mats, setMats] = useState([]);
  const [cafs, setCafs] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/material-requests").then((r) => setReqs(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMats(r.data)).catch(() => {});
    api.get("/cafeterias").then((r) => setCafs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setLoc("all"); }, [view]);

  const dateOk = (ts) => {
    const d = (ts || "").slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  const locOptions = view === "cafeteria" ? cafs.map((c) => c.name) : ["MIHAN", "MANEWADA"];

  const fOrders = useMemo(() => orders.filter((o) => dateOk(o.created_at)
    && (view !== "cafeteria" || loc === "all" || o.cafeteria_name === loc)
    && (view !== "kitchen" || loc === "all" || o.base_kitchen === loc)), [orders, view, loc, dateFrom, dateTo]); // eslint-disable-line
  const fReqs = useMemo(() => reqs.filter((r) => dateOk(r.created_at) && (loc === "all" || r.store === loc)), [reqs, loc, dateFrom, dateTo]); // eslint-disable-line

  const cookTotals = useMemo(() => {
    const agg = {};
    for (const o of fOrders) {
      if (o.status !== "Preparing") continue;
      for (const it of o.items) {
        const k = it.item_name + "|" + it.unit;
        agg[k] = (agg[k] || 0) + it.qty;
      }
    }
    return Object.entries(agg).map(([k, qty]) => ({ name: k.split("|")[0], unit: k.split("|")[1], qty }));
  }, [fOrders]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="flex gap-2">
          {[["cafeteria", "Cafeteria", Store], ["kitchen", "Kitchen", Flame], ["store", "Store", Warehouse]].map(([v, label, Icon]) => (
            <Button key={v} variant={view === v ? "default" : "outline"} size="sm" onClick={() => setView(v)} data-testid={`admin-ops-view-${v}`} className="gap-1.5">
              <Icon className="h-4 w-4" /> {label}
            </Button>
          ))}
        </div>
        <div className="lg:ml-auto flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{view === "cafeteria" ? "Cafeteria" : view === "kitchen" ? "Base Kitchen" : "Store"}</Label>
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger className="w-44" data-testid="admin-ops-loc-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {locOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" className="w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="admin-ops-date-from" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" className="w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="admin-ops-date-to" /></div>
        </div>
      </div>

      {/* Cafeteria board */}
      {view === "cafeteria" && (
        <Card className="p-5 border-orange-100 dark:border-slate-800" data-testid="admin-ops-cafeteria">
          <h3 className="font-heading font-bold mb-3">Cafeteria Orders ({fOrders.length})</h3>
          <div className="max-h-[520px] overflow-auto rounded-lg border border-border">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10"><TableRow>
                <TableHead>Cafeteria</TableHead><TableHead>Base</TableHead><TableHead>Deliver</TableHead>
                <TableHead>Placed</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Items</TableHead><TableHead className="text-right">Value</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {fOrders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No orders.</TableCell></TableRow>}
                {fOrders.map((o) => (
                  <TableRow key={o.id} data-testid="admin-ops-order-row">
                    <TableCell className="font-medium">{o.cafeteria_name}</TableCell>
                    <TableCell><Badge variant="secondary">{o.base_kitchen}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">{o.delivery_date ? `${o.delivery_date} · ` : ""}{o.delivery_time}{o.delivery_exact_time ? ` · ${o.delivery_exact_time}` : ""}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-right font-mono">{o.items.length}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-primary">{fmtMoney(o.total_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Kitchen board */}
      {view === "kitchen" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <Card className="p-5 border-orange-100 dark:border-slate-800 lg:col-span-2" data-testid="admin-ops-kitchen">
            <h3 className="font-heading font-bold mb-3">Kitchen Orders ({fOrders.length})</h3>
            <div className="max-h-[520px] overflow-auto rounded-lg border border-border">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10"><TableRow>
                  <TableHead>Base</TableHead><TableHead>Cafeteria</TableHead><TableHead>Deliver</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Value</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {fOrders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No orders.</TableCell></TableRow>}
                  {fOrders.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell><Badge variant="secondary">{o.base_kitchen}</Badge></TableCell>
                      <TableCell className="font-medium">{o.cafeteria_name}</TableCell>
                      <TableCell className="font-mono text-sm">{o.delivery_date ? `${o.delivery_date} · ` : ""}{o.delivery_time}{o.delivery_exact_time ? ` · ${o.delivery_exact_time}` : ""}</TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(o.total_value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
          <Card className="p-5 border-orange-100 dark:border-slate-800">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Scale className="h-4 w-4 text-primary" /> Cook Totals (pending)</h3>
            {cookTotals.length === 0 ? <p className="text-muted-foreground text-sm py-6 text-center">Nothing pending.</p> : (
              <div className="space-y-2">
                {cookTotals.map((t, i) => (
                  <div key={i} className="flex justify-between items-center rounded-lg border border-border bg-secondary/40 px-3 py-2">
                    <span className="text-sm font-medium">{t.name}</span>
                    <span className="font-mono font-bold text-primary">{t.qty} <span className="text-xs text-muted-foreground">{t.unit}</span></span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Store board */}
      {view === "store" && (
        <div className="grid lg:grid-cols-3 gap-5">
          <Card className="p-5 border-orange-100 dark:border-slate-800 lg:col-span-2" data-testid="admin-ops-store">
            <h3 className="font-heading font-bold mb-3">Material Requests ({fReqs.length})</h3>
            <div className="max-h-[520px] overflow-auto rounded-lg border border-border">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10"><TableRow>
                  <TableHead>Store</TableHead><TableHead>Requester</TableHead><TableHead>When</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Items</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {fReqs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No requests.</TableCell></TableRow>}
                  {fReqs.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell><Badge variant="secondary">{r.store}</Badge></TableCell>
                      <TableCell className="font-medium">{r.requested_by_name}<span className="text-xs text-muted-foreground capitalize block">{r.requester_role}</span></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                      <TableCell><Badge variant="outline">{r.status}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{r.items.length}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
          <Card className="p-5 border-orange-100 dark:border-slate-800">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Warehouse className="h-4 w-4 text-primary" /> Inventory</h3>
            {(() => {
              const sm = mats.filter((m) => loc === "all" || m.store === loc);
              const val = sm.reduce((s, m) => s + (m.stock || 0) * (m.rate || 0), 0);
              const low = sm.filter((m) => (m.stock || 0) <= (m.reorder_level ?? 10));
              return (
                <div className="space-y-3">
                  <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2"><p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Materials</p><p className="font-mono text-xl font-extrabold">{sm.length}</p></div>
                  <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2"><p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Stock Value</p><p className="font-mono text-xl font-extrabold">{fmtMoney(val)}</p></div>
                  <div className="rounded-lg border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-3 py-2"><p className="text-[10px] uppercase font-mono tracking-widest text-rose-600">Low Stock</p><p className="font-mono text-xl font-extrabold text-rose-700 dark:text-rose-300">{low.length}</p></div>
                </div>
              );
            })()}
          </Card>
        </div>
      )}
    </div>
  );
}

/* ---------------- Store Analytics ---------------- */
function StoreAnalytics() {
  const [store, setStore] = useState("MIHAN");
  const [timeframe, setTimeframe] = useState("weekly");
  const [data, setData] = useState(null);
  const [reqs, setReqs] = useState([]);

  useEffect(() => {
    api.get("/analytics/store", { params: { store, timeframe } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/material-requests", { params: { store } }).then((r) => setReqs(r.data)).catch(() => {});
  }, [store, timeframe]);

  const discrepancies = useMemo(() => {
    const rows = [];
    for (const r of reqs) {
      if (r.status !== "Received") continue;
      for (const it of r.items) {
        const iss = it.issued_qty || 0, rcv = it.received_qty || 0;
        if (iss !== rcv) rows.push({
          key: r.id + it.material_id, requester: r.requested_by_name, name: it.name, unit: it.unit,
          issued: iss, received: rcv, diff: rcv - iss, valueDiff: (rcv - iss) * (it.rate || 0),
          when: r.received_at || r.created_at,
        });
      }
    }
    return rows;
  }, [reqs]);

  const kpi = data?.kpi;
  const tiles = [
    { label: "Stock Value", value: fmtMoney(kpi?.stock_value), icon: Wallet, testid: "store-analytics-stock-value" },
    { label: "Materials", value: kpi?.item_count ?? 0, icon: Package, testid: "store-analytics-items" },
    { label: "Low Stock", value: kpi?.low_stock_count ?? 0, icon: AlertTriangle, testid: "store-analytics-low-count" },
    { label: "Consumption Value", value: fmtMoney(kpi?.consumption_value), icon: IndianRupee, testid: "store-analytics-consumption" },
    { label: "Transferred Out", value: kpi?.transferred_out ?? 0, icon: ArrowRightLeft, testid: "store-analytics-transferred" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          {["MIHAN", "MANEWADA"].map((s) => (
            <Button key={s} variant={store === s ? "default" : "outline"} size="sm" onClick={() => setStore(s)} data-testid={`store-analytics-store-${s.toLowerCase()}`}>{s}</Button>
          ))}
        </div>
        <div className="flex gap-2">
          {["daily", "weekly", "monthly"].map((tf) => (
            <Button key={tf} variant={timeframe === tf ? "default" : "outline"} size="sm" onClick={() => setTimeframe(tf)} data-testid={`store-analytics-tf-${tf}`} className="capitalize">{tf}</Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-5 border-orange-100 dark:border-slate-800" data-testid={t.testid}>
            <t.icon className="h-5 w-5 text-primary mb-3" />
            <p className="text-xs uppercase font-mono tracking-widest text-muted-foreground">{t.label}</p>
            <p className="font-mono text-xl font-extrabold mt-1">{t.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-orange-100 dark:border-slate-800">
          <h3 className="font-heading font-bold mb-4">Consumption Trend (qty issued)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data?.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
              <Line type="monotone" dataKey="qty" stroke="#ea580c" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 border-orange-100 dark:border-slate-800">
          <h3 className="font-heading font-bold mb-4">Top Consumed Materials (by value)</h3>
          {(data?.top_consumed || []).length === 0 ? (
            <p className="text-muted-foreground text-center py-16">No material issued in this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data?.top_consumed || []} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" fontSize={11} width={110} stroke="hsl(var(--muted-foreground))" />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="value" fill="#ea580c" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="p-5 border-orange-100 dark:border-slate-800">
        <h3 className="font-heading font-bold mb-4 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-600" /> Low Stock — Reorder Needed</h3>
        {(data?.low_stock || []).length === 0 ? (
          <p className="text-muted-foreground text-center py-8">All materials are above their reorder levels. 🎉</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Material</TableHead><TableHead className="text-right">Current Stock</TableHead>
              <TableHead className="text-right">Reorder Level</TableHead><TableHead className="text-right">Shortfall</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.low_stock.map((m) => (
                <TableRow key={m.name} data-testid="store-analytics-low-row">
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="text-right font-mono text-rose-600 font-semibold">{m.stock} {m.unit}</TableCell>
                  <TableCell className="text-right font-mono">{m.reorder_level} {m.unit}</TableCell>
                  <TableCell className="text-right font-mono">{Math.max(0, (m.reorder_level - m.stock)).toFixed(2)} {m.unit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="p-5 border-orange-100 dark:border-slate-800" data-testid="store-analytics-discrepancies">
        <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Receipt Discrepancies</h3>
        <p className="text-sm text-muted-foreground mb-4">Items where the cafeteria's received quantity differs from what the store issued.</p>
        {discrepancies.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No discrepancies — every receipt matched the issued quantity. ✅</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Requester</TableHead><TableHead>Material</TableHead>
              <TableHead className="text-right">Issued</TableHead><TableHead className="text-right">Received</TableHead>
              <TableHead className="text-right">Difference</TableHead><TableHead className="text-right">Value Impact</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {discrepancies.map((d) => (
                <TableRow key={d.key} data-testid="admin-discrepancy-row">
                  <TableCell className="font-medium">{d.requester}</TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell className="text-right font-mono">{d.issued} {d.unit}</TableCell>
                  <TableCell className="text-right font-mono">{d.received} {d.unit}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${d.diff < 0 ? "text-rose-600" : "text-emerald-600"}`}>{d.diff > 0 ? "+" : ""}{d.diff.toFixed(2)} {d.unit}</TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(d.valueDiff)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Items ---------------- */
const emptyItem = { name: "", category: "General", unit: "Kgs", rate: "", active: true };
function ItemsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem);

  const load = () => api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyItem); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, rate: String(it.rate) }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.rate) return toast.error("Name and rate required");
    const payload = { ...form, rate: Number(form.rate) };
    try {
      if (editing) await api.put(`/items/${editing.id}`, payload);
      else await api.post("/items", payload);
      toast.success("Item saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/items/${id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/items/upload", fd);
      toast.success(`Upload done: ${r.data.created} created, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const downloadTemplate = async (fmt) => {
    try {
      const r = await api.get("/items/template", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `baron_items_template.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Could not download template"); }
  };

  return (
    <Card className="p-5 border-orange-100 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading font-bold text-lg">Menu Items &amp; Rates</h3>
        <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" className="gap-2" onClick={() => downloadTemplate("csv")} data-testid="admin-items-template-csv-button"><Download className="h-4 w-4" /> CSV Template</Button>
        <Button variant="ghost" className="gap-2" onClick={() => downloadTemplate("xlsx")} data-testid="admin-items-template-xlsx-button"><Download className="h-4 w-4" /> Excel Template</Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} data-testid="admin-items-upload-input" />
        <Button variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-items-upload-button">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload CSV/Excel
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={openAdd} data-testid="admin-add-item-button"><Plus className="h-4 w-4" /> Add Item</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Item</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Name</Label>
                <Input data-testid="admin-item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Unit</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger data-testid="admin-item-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Kgs">Kgs</SelectItem><SelectItem value="Pcs">Pcs</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Rate (₹ per unit)</Label>
                  <Input type="number" data-testid="admin-item-rate-input" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="admin-item-submit-button">Save Item</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Unit</TableHead>
          <TableHead className="text-right">Rate</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id} data-testid="admin-item-row">
              <TableCell className="font-medium">{it.name}</TableCell>
              <TableCell className="text-muted-foreground">{it.category}</TableCell>
              <TableCell><Badge variant="secondary">{it.unit}</Badge></TableCell>
              <TableCell className="text-right font-mono"><span className="inline-flex items-center justify-end gap-0.5"><IndianRupee className="h-3 w-3" />{it.rate}</span></TableCell>
              <TableCell>{it.active ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(it.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function AdminMenuTab() {
  const [cafs, setCafs] = useState([]);
  useEffect(() => {
    api.get("/cafeterias").then((r) => setCafs(r.data)).catch(() => {});
  }, []);
  return <MenuViewer cafeterias={cafs} title="All kitchens" />;
}

/* ---------------- Cafeterias ---------------- */
const ACTION_STYLE = {
  Created: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Updated: "bg-blue-100 text-blue-800 border-blue-300",
  Deleted: "bg-rose-100 text-rose-800 border-rose-300",
  Uploaded: "bg-amber-100 text-amber-800 border-amber-300",
};

function ActivityLogTab() {
  const [logs, setLogs] = useState([]);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const params = {};
    if (entity !== "all") params.entity = entity;
    if (action !== "all") params.action = action;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/activity-logs", { params }).then((r) => setLogs(r.data)).catch(() => {});
  }, [entity, action, dateFrom, dateTo]);

  return (
    <Card className="p-5 border-orange-100 dark:border-slate-800" data-testid="admin-activity-log">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Admin Activity Log</h3>
        <div className="flex flex-wrap items-end gap-2">
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="w-36" data-testid="activity-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "Item", "Cafeteria", "Employee", "Material"].map((v) => <SelectItem key={v} value={v}>{v === "all" ? "All records" : v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-36" data-testid="activity-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "Created", "Updated", "Deleted", "Uploaded"].map((v) => <SelectItem key={v} value={v}>{v === "all" ? "All actions" : v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="activity-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="activity-date-to" />
        </div>
      </div>
      <div className="max-h-[560px] overflow-auto rounded-lg border border-border">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10"><TableRow>
            <TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>Record</TableHead><TableHead>Name</TableHead><TableHead>Details</TableHead><TableHead>By</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {logs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No activity recorded.</TableCell></TableRow>}
            {logs.map((l) => (
              <TableRow key={l.id} data-testid="activity-log-row">
                <TableCell className="text-xs whitespace-nowrap">{new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                <TableCell><Badge variant="outline" className={ACTION_STYLE[l.action]}>{l.action}</Badge></TableCell>
                <TableCell>{l.entity}</TableCell>
                <TableCell className="font-medium">{l.entity_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-md">{l.details || "—"}</TableCell>
                <TableCell className="text-sm">{l.by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

const emptyCaf = { name: "", location: "", contact: "", default_base_kitchen: "MIHAN", default_store: "MIHAN", active: true };
function CafeteriasTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCaf);

  const load = () => api.get("/cafeterias").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyCaf); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) await api.put(`/cafeterias/${editing.id}`, form);
      else await api.post("/cafeterias", form);
      toast.success("Cafeteria saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/cafeterias/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-5 border-orange-100 dark:border-slate-800">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-lg">Cafeteria Locations</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={openAdd} data-testid="admin-add-cafeteria-button"><Plus className="h-4 w-4" /> Add Cafeteria</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Cafeteria</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Name</Label>
                <Input data-testid="admin-cafeteria-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div className="space-y-2"><Label>Contact</Label>
                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
              <div className="space-y-2"><Label>Default Base Kitchen</Label>
                <Select value={form.default_base_kitchen} onValueChange={(v) => setForm({ ...form, default_base_kitchen: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MIHAN">MIHAN</SelectItem><SelectItem value="MANEWADA">MANEWADA</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Default Store</Label>
                <Select value={form.default_store || form.default_base_kitchen} onValueChange={(v) => setForm({ ...form, default_store: v })}>
                  <SelectTrigger data-testid="admin-cafeteria-store-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="MIHAN">MIHAN Store</SelectItem><SelectItem value="MANEWADA">MANEWADA Store</SelectItem></SelectContent>
                </Select></div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="admin-cafeteria-submit-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Location</TableHead><TableHead>Contact</TableHead>
          <TableHead>Base</TableHead><TableHead>Store</TableHead><TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.map((c) => (
            <TableRow key={c.id} data-testid="admin-cafeteria-row">
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell className="text-muted-foreground">{c.location}</TableCell>
              <TableCell className="font-mono text-sm">{c.contact}</TableCell>
              <TableCell><Badge variant="secondary">{c.default_base_kitchen}</Badge></TableCell>
              <TableCell><Badge variant="outline" data-testid="admin-cafeteria-store-badge">{c.default_store || c.default_base_kitchen}</Badge></TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ---------------- Employees ---------------- */
const emptyEmp = { name: "", username: "", password: "", role: "cafeteria", location: "" };
function EmployeesTab() {
  const [list, setList] = useState([]);
  const [cafeterias, setCafeterias] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEmp);

  const load = () => Promise.all([api.get("/employees"), api.get("/cafeterias")])
    .then(([e, c]) => { setList(e.data); setCafeterias(c.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyEmp); setOpen(true); };
  const openEdit = (emp) => { setEditing(emp); setForm({ ...emp, password: "" }); setOpen(true); };

  const locationOptions = ["kitchen", "store", "planner"].includes(form.role) ? ["MIHAN", "MANEWADA"] : cafeterias.map((c) => c.name);

  const save = async () => {
    if (!form.name || !form.username) return toast.error("Name and username required");
    if (!editing && !form.password) return toast.error("Password required");
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, location: form.location };
        if (form.password) payload.password = form.password;
        await api.put(`/employees/${editing.id}`, payload);
      } else {
        await api.post("/employees", form);
      }
      toast.success("Employee saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/employees/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-5 border-orange-100 dark:border-slate-800">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /><h3 className="font-heading font-bold text-lg">Staff Accounts</h3></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" onClick={openAdd} data-testid="admin-add-employee-button"><Plus className="h-4 w-4" /> Add Employee</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Employee</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Full Name</Label>
                <Input data-testid="admin-employee-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Username</Label>
                <Input value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
              <div className="space-y-2"><Label>{editing ? "New Password (leave blank to keep)" : "Password"}</Label>
                <Input type="text" data-testid="admin-employee-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, location: "" })}>
                    <SelectTrigger data-testid="admin-employee-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cafeteria">Cafeteria</SelectItem><SelectItem value="kitchen">Kitchen</SelectItem><SelectItem value="store">Store</SelectItem><SelectItem value="planner">Planner</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>{form.role === "kitchen" || form.role === "planner" ? "Base Kitchen" : form.role === "store" ? "Store" : "Cafeteria"}</Label>
                  <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                    <SelectTrigger data-testid="admin-employee-location-select"><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent>{locationOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="admin-employee-submit-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Username</TableHead><TableHead>Role</TableHead>
          <TableHead>Assigned To</TableHead><TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {list.map((e) => (
            <TableRow key={e.id} data-testid="admin-employee-row">
              <TableCell className="font-medium">{e.name}</TableCell>
              <TableCell className="font-mono text-sm">{e.username}</TableCell>
              <TableCell><Badge variant="secondary" className="capitalize">{e.role}</Badge></TableCell>
              <TableCell>{e.location || "—"}</TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="icon" onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(e.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
